import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { CountdownOverlay } from '../components/CountdownOverlay';
import { DezoomDots } from '../components/DezoomDots';
import { GuessForm } from '../components/GuessForm';
import { ZoomImage } from '../components/ZoomImage';
import type { PhotoAttributionData } from '../components/PhotoAttribution';
import { useGameCountdown } from '../hooks/useGameCountdown';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';
import { preloadImages } from '../utils/imageCache';

type Phase = 'lobby' | 'playing' | 'result';

interface ArenaStatus {
  eloRating: number;
  arenaMatchesPlayed: number;
  activeMatch: {
    id: string;
    currentRound: number;
    roundsTotal: number;
    opponentName: string;
    opponentElo: number;
    isGhost: boolean;
    status: string;
    eloDeltaA?: number;
    rounds?: { roundIndex: number; winnerSide: string | null }[];
  } | null;
}

interface RoundStartResponse {
  runId: string;
  startedAt?: string;
  roundIndex: number;
  puzzle: { id: string; puzzleNumber: number; attribution?: PhotoAttributionData | null };
  zoomLevels: { level: number; imageUrl: string }[];
  currentZoomLevel: number;
  resumed?: boolean;
}

interface GuessResponse {
  correct: boolean;
  status: string;
  dezoomsUsed: number;
  currentZoomLevel: number;
  score: number | null;
}

export function ArenaPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const friendId = params.get('friend');
  const { user, loading: authLoading } = useAuth();

  const [phase, setPhase] = useState<Phase>('lobby');
  const [status, setStatus] = useState<ArenaStatus | null>(null);
  const [matchId, setMatchId] = useState<string | null>(null);
  const [roundIndex, setRoundIndex] = useState(1);
  const [runId, setRunId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [zoomLevels, setZoomLevels] = useState<{ level: number; imageUrl: string }[]>([]);
  const [dezooms, setDezooms] = useState(0);
  const [attribution, setAttribution] = useState<PhotoAttributionData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [timerSec, setTimerSec] = useState(0);
  const [resultDelta, setResultDelta] = useState<number | null>(null);
  const [skipCountdown, setSkipCountdown] = useState(false);
  const [countdownEnabled, setCountdownEnabled] = useState(false);

  const loadStatus = useCallback(async () => {
    const data = await api<ArenaStatus>('/arena/status');
    setStatus(data);
    if (data.activeMatch?.status === 'active') {
      setMatchId(data.activeMatch.id);
      setRoundIndex(data.activeMatch.currentRound);
      setPhase('lobby');
    } else if (data.activeMatch?.status === 'completed') {
      setMatchId(data.activeMatch.id);
      setResultDelta(data.activeMatch.eloDeltaA ?? null);
      setPhase('result');
    }
    return data;
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.isPremium) {
      navigate('/premium', { replace: true });
      return;
    }
    loadStatus().catch((e) => setError(e.message));
  }, [authLoading, user, navigate, loadStatus]);

  const startRound = useCallback(async (mid: string) => {
    const data = await api<RoundStartResponse>(`/arena/match/${mid}/round/start`, { method: 'POST' });
    setRunId(data.runId);
    setRoundIndex(data.roundIndex);
    setZoomLevels(data.zoomLevels);
    setZoomLevel(data.currentZoomLevel ?? 0);
    setDezooms(0);
    setAttribution(data.puzzle.attribution ?? null);
    preloadImages(data.zoomLevels.map((z) => z.imageUrl));
    if (data.startedAt && data.resumed) setStartedAt(new Date(data.startedAt).getTime());
    setSkipCountdown(!!data.resumed);
    setCountdownEnabled(true);
    setPhase('playing');
  }, []);

  const startMatch = async (fid?: string | null) => {
    setBusy(true);
    setError('');
    try {
      const body = fid ? { friendId: fid } : {};
      const res = await api<{ match: { id: string; currentRound: number } }>('/arena/match', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setMatchId(res.match.id);
      setRoundIndex(res.match.currentRound);
      await loadStatus();
      await startRound(res.match.id);
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || t('arena.error'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (phase !== 'lobby' || !status || busy) return;
    if (friendId && !matchId) {
      startMatch(friendId);
    }
  }, [friendId, phase, status, matchId, busy]);

  const onGo = useCallback(async () => {
    if (!runId) return;
    const res = await api<{ startedAt: string }>(`/runs/${runId}/begin`, { method: 'POST' });
    setStartedAt(new Date(res.startedAt).getTime());
    setTimerSec(0);
  }, [runId]);

  const { display, revealed, ready } = useGameCountdown({
    enabled: countdownEnabled && phase === 'playing',
    skip: skipCountdown,
    onGo,
  });

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => {
      setTimerSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const handleGuess = async (guess: string): Promise<boolean | void> => {
    if (!matchId || !runId) return;
    setBusy(true);
    try {
      const res = await api<GuessResponse>(
        `/arena/match/${matchId}/round/${runId}/guess`,
        { method: 'POST', body: JSON.stringify({ guess }) },
      );
      setDezooms(res.dezoomsUsed);
      setZoomLevel(res.currentZoomLevel);

      if (res.status !== 'in_progress') {
        const matchData = await api<{ match: ArenaStatus['activeMatch'] }>(`/arena/match/${matchId}`);
        const m = matchData.match;
        if (m?.status === 'completed') {
          setResultDelta(m.eloDeltaA ?? null);
          setPhase('result');
          setCountdownEnabled(false);
          await loadStatus();
          return;
        }
        if (m && m.currentRound <= m.roundsTotal) {
          setRunId(null);
          setSkipCountdown(false);
          setCountdownEnabled(false);
          setPhase('lobby');
          setRoundIndex(m.currentRound);
          await loadStatus();
        }
      }
      return res.correct;
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || t('common.error'));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const continueMatch = async () => {
    if (!matchId) return;
    await startRound(matchId);
  };

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (!status) {
    return <main className="page play"><p className="loading">{error || t('common.loading')}</p></main>;
  }

  if (phase === 'result') {
    return (
      <main className="page arena-result">
        <div className="profile-card">
          <h1>{resultDelta != null && resultDelta >= 0 ? t('arena.win') : t('arena.loss')}</h1>
          {resultDelta != null && (
            <p className="arena-elo-delta">
              Elo {resultDelta >= 0 ? '+' : ''}{resultDelta}
            </p>
          )}
          <p className="muted">Elo actuel : {status.eloRating}</p>
          <button type="button" className="btn primary block" onClick={() => { setPhase('lobby'); setMatchId(null); startMatch(); }}>
            {t('arena.again')}
          </button>
          <Link to="/friends" className="link-muted">{t('friends.title')}</Link>
        </div>
      </main>
    );
  }

  if (phase === 'playing') {
    return (
      <main className="page play">
        <header className="play-header">
          <div className="play-header-left">
            <span className="puzzle-num practice-badge">
              {t('arena.round')} {roundIndex}/5
            </span>
            {startedAt != null && (
              <span className="play-timer">{formatTimer(timerSec)}</span>
            )}
          </div>
          <DezoomDots used={dezooms} />
        </header>
        <div className={`zoom-stage${revealed ? ' is-revealed' : ''}`}>
          <ZoomImage levels={zoomLevels} activeLevel={zoomLevel} attribution={attribution} />
          <CountdownOverlay value={display} />
        </div>
        <footer className="play-footer">
          {error && <p className="play-error" role="alert">{error}</p>}
          <GuessForm onSubmit={handleGuess} disabled={busy || !ready || !runId} />
        </footer>
      </main>
    );
  }

  return (
    <main className="page arena-lobby">
      <div className="profile-card">
        <h1>{t('arena.title')}</h1>
        <p className="arena-elo-big">Elo {status.eloRating}</p>
        <p className="muted">
          {status.arenaMatchesPlayed} {t('arena.matchesPlayed')}
        </p>

        {status.activeMatch && (
          <div className="arena-opponent">
            <p>{t('arena.vs')} <strong>{status.activeMatch.opponentName}</strong></p>
            <p className="muted">Elo {status.activeMatch.opponentElo}</p>
            <p>{t('arena.round')} {status.activeMatch.currentRound}/5</p>
            <button type="button" className="btn primary block" onClick={continueMatch} disabled={busy}>
              {t('arena.continue')}
            </button>
          </div>
        )}

        {!status.activeMatch && (
          <button type="button" className="btn primary block" onClick={() => startMatch()} disabled={busy}>
            {t('arena.findMatch')}
          </button>
        )}

        {error && <p className="error" role="alert">{error}</p>}

        <Link to="/friends" className="btn secondary block">{t('friends.title')}</Link>
        <Link to="/profile" className="link-muted">{t('landing.profile')}</Link>
      </div>
    </main>
  );
}
